import { honeypot } from "@honeypot-run/core"

export async function initHoneypot() {
  if (
    typeof window === "undefined" ||
    !process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ||
    !process.env.NEXT_PUBLIC_HONEYPOT_URL
  ) {
    return
  }

  // Wait for the page to finish loading
  if (document.readyState === "complete") {
    // Page is already loaded
    await initHoneypotWithRetry()
  } else {
    // Wait for the load event
    return new Promise<void>(resolve => {
      const handleLoad = async () => {
        window.removeEventListener("load", handleLoad)
        try {
          await initHoneypotWithRetry()
        } catch (error) {
          console.error("Honeypot: Failed to initialize after page load", error)
        }
        resolve()
      }

      window.addEventListener("load", handleLoad)
    })
  }
}

async function initHoneypotWithRetry(
  maxRetries = 3,
  baseDelay = 1000,
  timeoutMs = 3000,
): Promise<void> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Create a timeout promise that rejects after timeoutMs
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () =>
            reject(new Error(`Honeypot setup timeout after ${timeoutMs}ms`)),
          timeoutMs,
        )
      })

      // Race between the setup and the timeout
      await Promise.race([performHoneypotSetup(), timeoutPromise])

      console.log(`Honeypot: initialized successfully on attempt ${attempt}`)
      return
    } catch (error) {
      if (attempt === maxRetries) {
        console.error(
          `Honeypot: failed to initialize after ${maxRetries} attempts`,
          error,
        )
        // Don't throw - allow the app to continue working even if honeypot fails
        return
      }

      const delay = baseDelay * 2 ** (attempt - 1)
      console.warn(
        `Honeypot: attempt ${attempt} failed, retrying in ${delay}ms`,
        error,
      )

      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
}

async function performHoneypotSetup(): Promise<void> {
  const honeypotUrl = process.env.NEXT_PUBLIC_HONEYPOT_URL

  console.log(`Honeypot: starting setup with URL ${honeypotUrl}`)

  // The honeypot.setup() might hang if the script fails to load
  // We'll wrap it to ensure it doesn't block forever
  try {
    honeypot.setup({
      url: honeypotUrl,
    })

    console.log("Honeypot: setup() called, waiting for script to load...")

    // Give the script a moment to initialize
    // This is necessary because honeypot.setup() might return before the script is fully loaded
    await new Promise(resolve => setTimeout(resolve, 100))

    // If setup succeeds, add the amplitude plugin
    const { add: amplitudeAdd } = await import("@amplitude/analytics-browser")
    amplitudeAdd(
      honeypot.amplitudePlugin({
        includeEvents: [
          "Page Viewed",
          // Wallet events
          "Wallet Connected",
          "Wallet Disconnected",
          "Wallet Switch", // is this active?
          // Listing
          "Listing Start",
          "Listing Success",
          "Listing Error",
          "Listing Cancel",
          // Offers
          "Offer Accept Start",
          "Offer Accept Success",
          "Offer Accept Cancel",
          "Offer Create Start",
          "Offer Create Success",
          "Offer Create Cancel",
          // Purchases
          "Purchase Start",
          "Purchase Success",
          "Purchase Cancel",
          // Rewards
          "Rewards Claim Reward Error: Failed Security Check",
          "Rewards Claim Reward Attempt",
          "Rewards Claim Reward Success",
          "Rewards Claim Reward Error",
        ],
        trackIdentities: true,
      }),
    )

    console.log("Honeypot: Amplitude plugin added successfully")
  } catch (error) {
    console.error("Honeypot: Error during setup", error)
    throw error
  }
}

export enum HoneypotStatus {
  UNKNOWN = "unknown",
  HEADLESS_MODE = "headless_mode",
  COOKIES_DISABLED = "cookies_disabled",
  DEVICE_ID_AVAILABLE = "device_id_available",
  FAILED_TO_BUILD_SECURE_PAYLOAD = "failed_to_build_secure_payload",
}

export async function honeypotStatus(): Promise<HoneypotStatus> {
  if (typeof window === "undefined") {
    return HoneypotStatus.HEADLESS_MODE
  }

  try {
    await honeypot.track("cookie_check")
    return getHoneypotStatusWithRetry()
  } catch (error) {
    console.error("Failed to track honeypot status:", error)
    return HoneypotStatus.UNKNOWN
  }
}

async function getHoneypotStatusWithRetry(
  maxRetries = 4,
  baseDelay = 1000,
): Promise<HoneypotStatus> {
  let result: HoneypotStatus = HoneypotStatus.UNKNOWN
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      result = await getHoneypotStatus()
    } catch (error) {
      if (attempt === maxRetries) {
        throw error
      }
    }

    if (result === HoneypotStatus.DEVICE_ID_AVAILABLE) {
      return result
    }

    if (attempt === maxRetries) {
      console.error(`Client security: failed after ${maxRetries} attempts`)
      return result
    }

    const delay = baseDelay * 2 ** (attempt - 1)
    console.warn(
      `Client security: attempt ${attempt} failed, retrying in ${delay}ms`,
    )

    await honeypot.track("cookie_check: retry", { attempt })
    await new Promise(resolve => setTimeout(resolve, delay))
  }
  console.error(`Client security: failed after ${maxRetries} attempts`)
  return result
}

async function getHoneypotStatus(): Promise<HoneypotStatus> {
  const data = await honeypot.get()

  if (!(data.cookies_enabled || data.cookie_test_passed)) {
    console.error("Client security: cookies are not enabled")
    return HoneypotStatus.COOKIES_DISABLED
  }

  if (data.device_id || data.sealed) {
    return HoneypotStatus.DEVICE_ID_AVAILABLE
  }
  console.error("Client security: failed to build secure payload")
  return HoneypotStatus.FAILED_TO_BUILD_SECURE_PAYLOAD
}
